import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { config } from "@core/config/config";
import { GroupMemberRole } from "@domain/group/group";
import { useMyGroupWithFilter, useUserPublicSharerooms } from "@domain/group/hooks";
import { ContentLicense } from "@domain/licence/contentLicense";
import { Profile } from "@domain/user/profile";
import { useIsMobile } from "@utils/hooks";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Secondary } from "@ui/components/containers/textBlocks";
import { ShareroomThumbnail } from "@ui/components/group/shareroomThumbnail";
import { ShareroomThumbnailUtraSmall } from "@ui/components/group/shareroomThumbnailUtraSmall";
import { TagsView } from "@ui/components/inputs/tagsView";
import { Bloc, Row, Stack, row } from "@ui/components/layout";
import { ContentSelectorLicence } from "@ui/components/wallet/contentSelectorLicence";
import { ShareroomRoleFilter } from "@ui/pages/profilePage/content/shareroomRoleFliter";

interface GroupListProps extends React.HTMLAttributes<HTMLDivElement> {
	gap?: number;
	small?: boolean;
	size?: number;
	isMe?: boolean;
	profile?: Profile;
	isOnSide?: boolean;
}

const filterTypes: ContentLicense[] = ["PREMIUM", "FREE"];

export const GroupList: React.FC<GroupListProps> = ({
	gap = 20,
	small,
	size,
	isOnSide,
	profile,
	isMe,
	...htmlProps
}) => {
	const [memberRole, setMemberRole] = useState<GroupMemberRole | "ALL">("ALL");

	const { result: sharerooms, loading } =
		!isMe && profile
			? useUserPublicSharerooms(profile)
			: isMe && !profile
				? useMyGroupWithFilter("SHAREROOM", memberRole === "ALL" ? undefined : memberRole)
				: { result: [], loading: false };
	const displaySharerooms = size ? sharerooms.slice(0, size) : sharerooms;

	const [selectedFilter, setSelectedFilter] = useState(filterTypes[0]);
	const isMobile = useIsMobile();

	// if (totalElements === 0) {
	// 	return null;
	//

	function totalCount(): { countPremium: number; countFree: number } {
		const premiumShareroom = displaySharerooms.find((shareroom) => shareroom.license === "PREMIUM");
		const totalPremium = premiumShareroom ? 1 : 0;
		const freeShareroom = displaySharerooms.find((shareroom) => shareroom.license === "FREE");
		const totalFree = freeShareroom ? 1 : 0;
		return {
			countPremium: totalPremium,
			countFree: totalFree,
		};
	}

	const resultFilter = totalCount();

	const countPremium = resultFilter.countPremium;
	const countFree = resultFilter.countFree;

	return !isOnSide ? (
		<>
			{!isMobile ? (
				config.features.classrooms.enablePremium ? (
					<Row justify={"space-between"}>
						<Bloc>
							<TagsView
								tags={filterTypes}
								selectedTag={selectedFilter as string}
								onItemClick={(filter: string) => {
									setSelectedFilter(filter as ContentLicense);
								}}
							/>
						</Bloc>
						<Bloc style={{ marginTop: 21 }}>
							<ShareroomRoleFilter
								memberRole={memberRole}
								onRoleChange={(newMemberRole) => setMemberRole(newMemberRole)}
							/>
						</Bloc>
					</Row>
				) : null
			) : (
				<Filters>
					{config.features.classrooms.enablePremium && (
						<ContentSelectorLicence currentContentLicence={selectedFilter} onContentChange={setSelectedFilter} />
					)}
					<ShareroomRoleFilter memberRole={memberRole} onRoleChange={(newMemberRole) => setMemberRole(newMemberRole)} />
				</Filters>
			)}

			{config.features.classrooms.enablePremium && selectedFilter === "PREMIUM" ? (
				<>
					<Stack gap={gap} {...htmlProps}>
						{!loading &&
							countPremium > 0 &&
							displaySharerooms.map((shareroom) =>
								shareroom.license === "PREMIUM" ? (
									<Thumbnail key={shareroom?.id} shareroom={shareroom} small={small} />
								) : null
							)}
						{!loading && countPremium === 0 && (
							<Secondary>
								{I18n.t(
									memberRole === "ALL"
										? `profile${isMe ? ".self" : ""}.shareroom_premium.empty_all`
										: memberRole === "MEMBER"
											? `profile${isMe ? ".self" : ""}.shareroom_premium_member.empty`
											: `profile${isMe ? ".self" : ""}.shareroom_premium_administrator.empty`,
									{ name: `${profile?.primaryName} ${profile?.secondaryName}` }
								)}
							</Secondary>
						)}
						{loading && <ClipLoader />}
					</Stack>
				</>
			) : null}

			{!config.features.classrooms.enablePremium || selectedFilter === "FREE" ? (
				<>
					<Stack gap={gap} {...htmlProps}>
						{!loading &&
							countFree > 0 &&
							displaySharerooms.map((shareroom) =>
								shareroom.license === "FREE" ? (
									<Thumbnail key={shareroom?.id} shareroom={shareroom} small={small} />
								) : null
							)}
						{!loading && countFree === 0 && (
							<Secondary>
								{I18n.t(
									memberRole === "ALL"
										? `profile${isMe ? ".self" : ""}.shareroom_free.empty_all`
										: memberRole === "MEMBER"
											? `profile${isMe ? ".self" : ""}.shareroom_free_member.empty`
											: `profile${isMe ? ".self" : ""}.shareroom_free_administrator.empty`,
									{ name: `${profile?.primaryName} ${profile?.secondaryName}` }
								)}
							</Secondary>
						)}
						{loading && <ClipLoader />}
					</Stack>
				</>
			) : null}
		</>
	) : (
		<>
			{loading ? (
				<ClipLoader />
			) : (
				<Container>
					<Stack gap={30}>
						{displaySharerooms &&
							displaySharerooms.map((shareroom) =>
								shareroom.license === "FREE" || shareroom.license === "PREMIUM" ? (
									<ShareroomThumbnailUtraSmall key={shareroom?.id} shareroom={shareroom} />
								) : null
							)}
						{loading && <ClipLoader />}
					</Stack>
				</Container>
			)}
		</>
	);
};

const Thumbnail = styled(ShareroomThumbnail)`
	width: 100%;
`;

const Container = styled(RoundedBlock)`
	padding: 10px;
	width: 100%;
`;
const Filters = styled.div`
	${row(20, "center", "center")}
	align-self: flex-start;
	margin-bottom: 30px;
	justify-content: space-between;
`;
